/**
 * Componentes de interface compartilhados por todas as telas do painel.
 * O visual vem de styles/app.css; aqui ficam só estrutura e comportamento.
 */
import { cloneElement, isValidElement, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ChevronLeft, ChevronRight, ImageOff, Inbox, Search, X } from "lucide-react";
import { initials } from "../lib/format";

const cx = (...c) => c.filter(Boolean).join(" ");

/* ── Estrutura de página ───────────────────────────────────────────── */

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <div className="page-header__eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function Card({ title, actions, children, className, bodyClassName = "card__body", accent }) {
  return (
    <section className={cx("card", accent && "card--accent", className)}>
      {(title || actions) && (
        <div className="card__head">
          <div className="card__title">{title}</div>
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {bodyClassName === null ? children : <div className={bodyClassName}>{children}</div>}
    </section>
  );
}

/* ── Botões ────────────────────────────────────────────────────────── */

export function Button({ variant = "secondary", size, icon: Icon, loading, block, className, children, ...props }) {
  const iconOnly = !children;
  return (
    <button
      type="button"
      className={cx(
        "btn",
        `btn--${variant}`,
        size && `btn--${size}`,
        iconOnly && "btn--icon",
        block && "btn--block",
        className,
      )}
      disabled={loading || props.disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <span className="spinner spinner--sm" /> : Icon && <Icon size={size === "sm" ? 15 : 17} />}
      {children}
    </button>
  );
}

/* ── Campos ────────────────────────────────────────────────────────── */

/** Rótulo + controle + dica. O controle recebe o id automaticamente. */
export function Field({ label, required, hint, children, className }) {
  const id = useId();
  const child = typeof children === "function" ? children(id) : children;
  return (
    <div className={cx("field", className)}>
      {label && (
        <label className="field__label" htmlFor={id}>
          {label} {required && <span className="req">*</span>}
        </label>
      )}
      {typeof children === "function" ? child : withId(child, id, required)}
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

function withId(el, id, required) {
  return isValidElement(el) ? cloneElement(el, { id, required, ...el.props }) : el;
}

export const Input = ({ className, ...p }) => <input className={cx("input", className)} {...p} />;
export const Textarea = ({ className, ...p }) => <textarea className={cx("textarea", className)} {...p} />;

export function Select({ options, placeholder, className, ...p }) {
  return (
    <select className={cx("select", className)} {...p}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const { value, label } = typeof o === "string" ? { value: o, label: o } : o;
        return (
          <option key={value} value={value}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

export function Switch({ checked, onChange, label, description, icon: Icon }) {
  return (
    <button type="button" role="switch" aria-checked={checked} className="switch" onClick={() => onChange(!checked)}>
      <span className="row" style={{ flexWrap: "nowrap" }}>
        {Icon && <Icon size={18} color={checked ? "var(--red-light)" : "var(--muted)"} />}
        <span>
          <span className="strong">{label}</span>
          {description && <span className="small muted" style={{ display: "block" }}>{description}</span>}
        </span>
      </span>
      <span className="switch__track" />
    </button>
  );
}

/** Abas compactas. `options`: [{ value, label, icon? }]. */
export function Segmented({ value, onChange, options, label }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(({ value: v, label: l, icon: Icon }) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>
          {Icon && <Icon size={15} />}
          {l}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Buscar…" }) {
  return (
    <div className="search">
      <Search size={16} />
      <input
        className="input"
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
}

/* ── Selos e mídia ─────────────────────────────────────────────────── */

export const Badge = ({ tone, children, className }) => (
  <span className={cx("badge", tone && `badge--${tone}`, className)}>{children}</span>
);

export function Avatar({ src, name, size }) {
  const [broken, setBroken] = useState(false);
  const cls = cx("avatar", size && `avatar--${size}`);
  return src && !broken ? (
    <img className={cls} src={src} alt="" onError={() => setBroken(true)} />
  ) : (
    <span className={cls} aria-hidden>
      {initials(name)}
    </span>
  );
}

/** Imagem com fallback quando a URL falha ou está vazia. */
export function Thumb({ src, alt = "", size, className, icon: Icon = ImageOff, contain }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const cls = cx("thumb", size && `thumb--${size}`, className);
  return src && !broken ? (
    <img className={cls} src={src} alt={alt} onError={() => setBroken(true)} style={contain ? { objectFit: "contain" } : undefined} />
  ) : (
    <span className={cls}>
      <Icon size={18} />
    </span>
  );
}

/* ── Estados ───────────────────────────────────────────────────────── */

export const Spinner = ({ small }) => <span className={cx("spinner", small && "spinner--sm")} role="status" />;

export const Loader = ({ label = "Carregando…", page }) => (
  <div className={cx("loader", page && "loader--page")}>
    <Spinner /> {label}
  </div>
);

export function EmptyState({ icon: Icon = Inbox, title, children, action }) {
  return (
    <div className="empty">
      <span className="empty__icon">
        <Icon size={24} />
      </span>
      {title && <strong>{title}</strong>}
      {children && <span>{children}</span>}
      {action}
    </div>
  );
}

export function Alert({ tone, children, icon: Icon = AlertTriangle }) {
  return (
    <div className={cx("alert", tone && `alert--${tone}`)}>
      <Icon size={16} />
      <div>{children}</div>
    </div>
  );
}

/* ── Modal e confirmação ───────────────────────────────────────────── */

export function Modal({ open, title, onClose, children, footer, size }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={cx("modal", size && `modal--${size}`)} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal__head">
          <div className="modal__title">{title}</div>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Fechar" />
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Pede confirmação antes de uma ação destrutiva.
 * `onConfirm` pode ser assíncrono; o botão mostra carregamento até terminar.
 */
export function ConfirmDialog({ open, title, children, confirmLabel = "Excluir", tone = "primary", onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const confirmar = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      title={title}
      onClose={busy ? undefined : onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant={tone} onClick={confirmar} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="muted">{children}</div>
    </Modal>
  );
}

/* ── Paginação ─────────────────────────────────────────────────────── */

/** Divide `items` em páginas; volta à página 1 quando a lista muda de tamanho. */
export function usePagination(items, perPage) {
  const [page, setPage] = useState(1);
  const total = Math.max(1, Math.ceil(items.length / perPage));
  useEffect(() => setPage(1), [items.length]);
  const atual = Math.min(page, total);
  return {
    page: atual,
    total,
    setPage,
    items: items.slice((atual - 1) * perPage, atual * perPage),
  };
}

export function Pagination({ page, total, setPage }) {
  if (total <= 1) return null;
  return (
    <nav className="pagination" aria-label="Paginação">
      <Button size="sm" icon={ChevronLeft} disabled={page === 1} onClick={() => setPage(page - 1)} aria-label="Anterior" />
      <span className="small muted">
        Página {page} de {total}
      </span>
      <Button size="sm" icon={ChevronRight} disabled={page === total} onClick={() => setPage(page + 1)} aria-label="Próxima" />
    </nav>
  );
}
